import httpClient from '../http/http-client';
import {
  IAddGroupItem,
  ICreateEmissionRule,
  ICreateSurvey,
  ICreateSurveyInvitationRequest,
  ICreateSurveyQuestion,
  ICreateSurveyQuestionGroup,
  ICreateSurveyQuestionOption,
  ISurvey,
  ISurveyCommittedEmissionsRemovalPreview,
  ISurveyInvitation,
  ISurveyQuestion,
  ISurveyQuestionGroup,
  ISurveyQuestionGroupEmissionRule,
  ISurveyQuestionGroupItem,
  ISurveyQuestionType,
  ISurveySession,
  ISurveySessionDetail,
  ISurveyProvisionalEmissionAggregateRow,
  ISurveyProvisionalEmissionRow,
  ISurveyCommitEmissionsPreview,
  IUpdateSurvey,
} from './survey.model';

export default class SurveyAdminService {
  http;

  static instance = new SurveyAdminService();

  static getInstance() {
    if (SurveyAdminService.instance === null) SurveyAdminService.instance = new SurveyAdminService();
    return this.instance;
  }

  constructor() {
    this.http = httpClient;
  }

  // --- Question Types ---

  async getQuestionTypes(): Promise<ISurveyQuestionType[]> {
    return this.http.get('survey-question-type')
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Questions ---

  async getQuestions(params?: { active?: boolean; surveyQuestionTypeId?: string; page?: number; perPage?: number }): Promise<{ count: number; rows: ISurveyQuestion[] }> {
    const query = new URLSearchParams();
    if (params?.active !== undefined) query.append('active', String(params.active));
    if (params?.surveyQuestionTypeId) query.append('surveyQuestionTypeId', params.surveyQuestionTypeId);
    if (params?.page !== undefined) query.append('page', String(params.page));
    if (params?.perPage !== undefined) query.append('perPage', String(params.perPage));
    const qs = query.toString();
    return this.http.get(`survey-question${qs ? `?${qs}` : ''}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createQuestion(data: ICreateSurveyQuestion): Promise<ISurveyQuestion> {
    return this.http.post('survey-question', data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateQuestion(id: string, data: Partial<ICreateSurveyQuestion>): Promise<ISurveyQuestion> {
    return this.http.patch(`survey-question/${id}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteQuestion(id: string): Promise<void> {
    return this.http.delete(`survey-question/${id}`)
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Question Options ---

  async addOption(questionId: string, data: ICreateSurveyQuestionOption): Promise<ISurveyQuestion> {
    return this.http.post(`survey-question/${questionId}/options`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateOption(questionId: string, optionId: string, data: Partial<ICreateSurveyQuestionOption>): Promise<ISurveyQuestion> {
    return this.http.patch(`survey-question/${questionId}/options/${optionId}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteOption(questionId: string, optionId: string): Promise<void> {
    return this.http.delete(`survey-question/${questionId}/options/${optionId}`)
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Question Groups ---

  async getGroups(params?: { active?: boolean }): Promise<ISurveyQuestionGroup[]> {
    const qs = params?.active !== undefined ? `?active=${params.active}` : '';
    return this.http.get(`survey-question-group${qs}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getGroup(id: string): Promise<ISurveyQuestionGroup> {
    return this.http.get(`survey-question-group/${id}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createGroup(data: ICreateSurveyQuestionGroup): Promise<ISurveyQuestionGroup> {
    return this.http.post('survey-question-group', data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateGroup(id: string, data: Partial<ICreateSurveyQuestionGroup>): Promise<ISurveyQuestionGroup> {
    return this.http.patch(`survey-question-group/${id}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteGroup(id: string): Promise<void> {
    return this.http.delete(`survey-question-group/${id}`)
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Group Items ---

  async addGroupItem(groupId: string, data: IAddGroupItem): Promise<ISurveyQuestionGroupItem> {
    return this.http.post(`survey-question-group/${groupId}/items`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateGroupItem(groupId: string, itemId: string, data: Partial<IAddGroupItem>): Promise<ISurveyQuestionGroupItem> {
    return this.http.patch(`survey-question-group/${groupId}/items/${itemId}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateGroupItemPages(groupId: string, updates: { id: string; pageNumber: number }[]): Promise<void> {
    return this.http.post(`survey-question-group/${groupId}/items/update-pages`, { updates })
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  async removeGroupItem(groupId: string, itemId: string): Promise<void> {
    return this.http.delete(`survey-question-group/${groupId}/items/${itemId}`)
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Emission Rules ---

  async createEmissionRule(groupId: string, data: ICreateEmissionRule): Promise<ISurveyQuestionGroupEmissionRule> {
    return this.http.post(`survey-question-group/${groupId}/emission-rules`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateEmissionRule(groupId: string, ruleId: string, data: Partial<ICreateEmissionRule>): Promise<ISurveyQuestionGroupEmissionRule> {
    return this.http.patch(`survey-question-group/${groupId}/emission-rules/${ruleId}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteEmissionRule(groupId: string, ruleId: string): Promise<void> {
    return this.http.delete(`survey-question-group/${groupId}/emission-rules/${ruleId}`)
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Surveys ---

  async getSurveys(params?: { status?: string; surveyQuestionGroupId?: string; withCounts?: boolean; page?: number; perPage?: number }): Promise<{ count: number; rows: ISurvey[] }> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.surveyQuestionGroupId) query.append('surveyQuestionGroupId', params.surveyQuestionGroupId);
    if (params?.withCounts) query.append('withCounts', 'true');
    if (params?.page !== undefined) query.append('page', String(params.page));
    if (params?.perPage !== undefined) query.append('perPage', String(params.perPage));
    const qs = query.toString();
    return this.http.get(`survey${qs ? `?${qs}` : ''}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSurvey(id: string): Promise<ISurvey> {
    return this.http.get(`survey/${id}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createSurvey(data: ICreateSurvey): Promise<ISurvey> {
    return this.http.post('survey', data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateSurvey(id: string, data: IUpdateSurvey): Promise<ISurvey> {
    return this.http.patch(`survey/${id}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteSurvey(id: string): Promise<void> {
    return this.http.delete(`survey/${id}`)
      .then(() => undefined)
      .catch(err => { console.log(err); throw err; });
  }

  async rotateOpenLinkToken(id: string): Promise<{ openLinkToken: string }> {
    return this.http.post(`survey/${id}/rotate-token`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Invitations ---

  async getInvitations(surveyId: string, params?: { page?: number; perPage?: number; sentOnly?: boolean }): Promise<{ count: number; rows: ISurveyInvitation[] }> {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.append('page', String(params.page));
    if (params?.perPage !== undefined) query.append('perPage', String(params.perPage));
    if (params?.sentOnly === true) query.append('sentOnly', 'true');
    const qs = query.toString();
    return this.http.get(`survey/${surveyId}/invitations${qs ? `?${qs}` : ''}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createInvitations(surveyId: string, payload: ICreateSurveyInvitationRequest): Promise<ISurveyInvitation[]> {
    return this.http.post(`survey/${surveyId}/invitations`, payload)
      .then(response => Array.isArray(response.data) ? response.data : response.data.invitations)
      .catch(err => { console.log(err); throw err; });
  }

  async getSupplierCoverage(surveyId: string): Promise<import('./survey.model').ISurveySupplierCoverage> {
    return this.http.get(`survey/${surveyId}/supplier-coverage`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  /**
   * Queues the invitation email again for invitations that have already been sent and
   * are not completed. With `invitationIds` the send targets just those invitations
   * (the backend applies the same sent/not-completed predicate); without it, every
   * eligible invitation on the survey is reminded.
   */
  async remindInvitations(surveyId: string, options?: { customEmailText?: string; invitationIds?: string[] }): Promise<{ reminded: number }> {
    return this.http.post(`survey/${surveyId}/invitations/remind`, {
      customEmailText: options?.customEmailText,
      invitationIds: options?.invitationIds,
    })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async regenerateInviteToken(surveyId: string, invitationId: string): Promise<{ inviteToken: string }> {
    return this.http.post(`survey/${surveyId}/invitations/${invitationId}/regenerate-token`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Sessions / Responses ---

  async getSessions(surveyId: string, submittedOnly?: boolean): Promise<{ count: number; rows: ISurveySession[] }> {
    const qs = submittedOnly ? '?submittedOnly=true' : '';
    return this.http.get(`survey/${surveyId}/sessions${qs}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async invalidateSurveySession(sessionId: string): Promise<ISurveySession> {
    return this.http.post(`survey/sessions/${sessionId}/invalidate`, {})
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async reinstateSurveySession(sessionId: string): Promise<ISurveySession> {
    return this.http.post(`survey/sessions/${sessionId}/reinstate`, {})
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteSurveySession(sessionId: string): Promise<{ message: string; deleted: boolean }> {
    return this.http.delete(`survey/sessions/${sessionId}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSessionDetail(sessionId: string): Promise<ISurveySessionDetail> {
    return this.http.get(`survey/sessions/${sessionId}/responses`)
      .then(response => {
        const data = response.data;
        // API returns { session: {...}, items: [...] }
        if (data.session && data.items !== undefined) {
          return { ...data.session, responseItems: data.items };
        }
        return data;
      })
      .catch(err => { console.log(err); throw err; });
  }

  async getSessionProvisionalEmissions(sessionId: string): Promise<{
    rows: ISurveyProvisionalEmissionRow[];
    invalidated?: boolean;
    message?: string;
  }> {
    return this.http.get(`survey/sessions/${sessionId}/provisional-emissions`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSurveyProvisionalEmissionsAggregate(surveyId: string): Promise<{
    sessionCount: number;
    rows: ISurveyProvisionalEmissionAggregateRow[];
  }> {
    return this.http.get(`survey/${surveyId}/provisional-emissions-aggregate`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSurveyCommitEmissionsPreview(surveyId: string): Promise<ISurveyCommitEmissionsPreview> {
    return this.http.get(`survey/${surveyId}/commit-emissions-preview`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async commitSurveyEmissions(surveyId: string): Promise<{
    message: string;
    emissionsCreated: number;
    committedSessionCount: number;
  }> {
    return this.http.post(`survey/${surveyId}/commit-emissions`, {})
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSurveyCommittedEmissionsRemovalPreview(surveyId: string): Promise<ISurveyCommittedEmissionsRemovalPreview> {
    return this.http.get(`survey/${surveyId}/committed-emissions-removal-preview`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async removeSurveyCommittedEmissions(surveyId: string): Promise<{
    message: string;
    deletedCalculationCount: number;
    deletedEntireEmissionCount: number;
    sessionsMarkedUncommitted: number;
  }> {
    return this.http.post(`survey/${surveyId}/remove-committed-emissions`, {})
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSurveyAnalyticsDashboard(reportPeriodId: string): Promise<import('./survey.model').ISurveyAnalyticsDashboard> {
    return this.http.get(`survey/analytics-dashboard?reportPeriodId=${encodeURIComponent(reportPeriodId)}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getSurveyCommittedEmissionsSummary(surveyId: string): Promise<import('./survey.model').ISurveyCommittedEmissionsSummary> {
    return this.http.get(`survey/${surveyId}/committed-emissions-summary`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getExtrapolation(surveyId: string): Promise<import('./survey.model').ISurveyExtrapolation | null> {
    return this.http.get(`survey/${surveyId}/extrapolation`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async previewExtrapolation(surveyId: string, targetResponseCount: number): Promise<import('./survey.model').ISurveyExtrapolationPreview> {
    return this.http.post(`survey/${surveyId}/extrapolation/preview`, { targetResponseCount })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createExtrapolation(surveyId: string, targetResponseCount: number): Promise<{ message: string; extrapolationId: string; emissionsCreated: number; scaleFactor: number }> {
    return this.http.post(`survey/${surveyId}/extrapolation`, { targetResponseCount })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async reverseExtrapolation(surveyId: string): Promise<{ message: string; emissionsRemoved: number }> {
    return this.http.delete(`survey/${surveyId}/extrapolation`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }
  async getProductDataPreview(surveyId: string): Promise<import('./survey.model').ISurveyProductDataPreview> {
    return this.http.get(`survey/${surveyId}/product-data-preview`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async applyProductData(surveyId: string, productResponseIds: string[], confirmEpdOverwriteGoodIds?: string[]): Promise<import('./survey.model').ISurveyApplyProductDataResult> {
    return this.http.post(`survey/${surveyId}/apply-product-data`, { productResponseIds, confirmEpdOverwriteGoodIds })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

}
